<template>
  <SettlementDrawerShell
    :show="show"
    title="结算预览"
    description="支持先查看任意月份明细；只有进入结算窗口的月份才允许确认月结。"
    @close="emit('close')"
  >
    <div v-if="loading" class="flex min-h-[320px] items-center justify-center">
      <div class="text-center">
        <div class="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-indigo-100 border-t-indigo-500" />
        <p class="mt-4 text-sm text-gray-500 dark:text-gray-400">正在加载结算明细...</p>
      </div>
    </div>

    <div v-else-if="preview" class="space-y-6">
      <section class="grid grid-cols-1 gap-4 rounded-2xl bg-gray-50 p-4 dark:bg-gray-900/50 md:grid-cols-2">
        <div>
          <p class="text-xs font-medium uppercase tracking-wide text-gray-400">租户</p>
          <p class="mt-1 text-base font-semibold text-gray-900 dark:text-gray-100">{{ tenantName }}</p>
        </div>
        <div>
          <p class="text-xs font-medium uppercase tracking-wide text-gray-400">结算月份</p>
          <p class="mt-1 text-base font-semibold text-gray-900 dark:text-gray-100">{{ preview.settlementMonth }}</p>
        </div>
        <div>
          <p class="text-xs font-medium uppercase tracking-wide text-gray-400">记录数</p>
          <p class="mt-1 text-base font-semibold text-gray-900 dark:text-gray-100">{{ preview.recordCount }}</p>
        </div>
        <div>
          <p class="text-xs font-medium uppercase tracking-wide text-gray-400">应结租户</p>
          <p class="mt-1 text-lg font-semibold text-emerald-600 dark:text-emerald-400">{{ formatCurrency(preview.tenantAmount) }}</p>
        </div>
      </section>

      <section class="grid grid-cols-1 gap-4 rounded-2xl border border-gray-200 p-4 dark:border-gray-700 md:grid-cols-3">
        <div>
          <p class="text-xs font-medium uppercase tracking-wide text-gray-400">用户实付</p>
          <p class="mt-1 text-base font-semibold text-gray-900 dark:text-gray-100">{{ formatCurrency(preview.grossAmount) }}</p>
        </div>
        <div>
          <p class="text-xs font-medium uppercase tracking-wide text-gray-400">平台留存</p>
          <p class="mt-1 text-base font-semibold text-gray-900 dark:text-gray-100">{{ formatCurrency(preview.platformAmount) }}</p>
        </div>
        <div>
          <p class="text-xs font-medium uppercase tracking-wide text-gray-400">应结租户</p>
          <p class="mt-1 text-base font-semibold text-emerald-600 dark:text-emerald-400">{{ formatCurrency(preview.tenantAmount) }}</p>
        </div>
      </section>

      <section
        class="rounded-2xl px-4 py-3 text-sm"
        :class="preview.canSettle
          ? 'border border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200'
          : 'border border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-900/60 dark:bg-sky-950/30 dark:text-sky-200'"
      >
        {{ preview.settlementHint || '仅包含该月内未结算的租户账本记录。确认后将生成月结批次并锁定本次明细。' }}
      </section>

      <section class="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div>
          <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">打款流水号</label>
          <input
            :value="paymentReference"
            type="text"
            placeholder="例如 BANK-20260410-001"
            class="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:focus:ring-indigo-900"
            :disabled="!preview.canSettle"
            @input="emit('update:paymentReference', ($event.target as HTMLInputElement).value)"
          />
        </div>
        <div>
          <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">结算备注</label>
          <input
            :value="note"
            type="text"
            placeholder="例如 4 月月结，线下银行转账"
            class="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:focus:ring-indigo-900"
            :disabled="!preview.canSettle"
            @input="emit('update:note', ($event.target as HTMLInputElement).value)"
          />
        </div>
      </section>

      <section class="overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700">
        <div class="border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-900/50">
          <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">结算明细</h3>
        </div>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead class="bg-white dark:bg-gray-800">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">记录ID</th>
                <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">来源</th>
                <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">订单号</th>
                <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">用户ID</th>
                <th class="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">用户实付</th>
                <th class="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">平台留存</th>
                <th class="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">应结租户</th>
                <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">生效时间</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
              <tr v-for="record in preview.records" :key="record.id" class="hover:bg-gray-50 dark:hover:bg-gray-900/40">
                <td class="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">{{ record.id }}</td>
                <td class="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{{ getSourceLabel(record.sourceType, record.recordKind) }} #{{ record.sourceId }}</td>
                <td class="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{{ record.sourceOrderNo }}</td>
                <td class="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{{ record.userId }}</td>
                <td class="px-4 py-3 text-right text-sm text-gray-500 dark:text-gray-400">{{ formatCurrency(record.grossAmount) }}</td>
                <td class="px-4 py-3 text-right text-sm text-gray-500 dark:text-gray-400">{{ formatCurrency(record.platformAmount) }}</td>
                <td class="px-4 py-3 text-right text-sm font-medium text-emerald-600 dark:text-emerald-400">{{ formatCurrency(record.tenantAmount) }}</td>
                <td class="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{{ formatDateTime(record.paidAt) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <template #footer>
      <div class="flex items-center justify-end gap-3">
        <button
          type="button"
          class="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
          :disabled="submitting"
          @click="emit('close')"
        >
          取消
        </button>
        <button
          type="button"
          class="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="!preview || preview.recordCount === 0 || !preview.canSettle || submitting || loading"
          @click="emit('confirm')"
        >
          {{
            submitting
              ? '结算中...'
              : !preview
                ? '确认结算'
                : preview.canSettle
                  ? `确认结算 ${formatCurrency(preview.tenantAmount)}`
                  : `当前最多可结算到 ${preview.allowedSettlementMonth}`
          }}
        </button>
      </div>
    </template>
  </SettlementDrawerShell>
</template>

<script setup lang="ts">
import SettlementDrawerShell from '@/views/admin/components/tenant-settlement/SettlementDrawerShell.vue'
import type { TenantSettlementPreviewResponse } from '@/types/admin/tenant'
import { formatCurrency, formatDateTime } from '@/utils/formatters'

interface Props {
  show: boolean
  tenantName: string
  preview: TenantSettlementPreviewResponse | null
  paymentReference: string
  note: string
  loading: boolean
  submitting: boolean
}

defineProps<Props>()

const emit = defineEmits<{
  close: []
  confirm: []
  'update:paymentReference': [value: string]
  'update:note': [value: string]
}>()

function getSourceLabel(sourceType: string, recordKind: string): string {
  const sourceLabel = sourceType === 'TUTOR_LESSON' ? '约课' : '会员'
  return recordKind === 'REFUND_ADJUSTMENT' ? `${sourceLabel}退款调整` : sourceLabel
}
</script>
